/**
 * Story 首免/自动拍策略只读与领取（C 域）SDK。
 *
 * 只包装 C 已稳定的 HTTP 合同：本人资格/授权状态、一次性 claim、关闭回执。
 * 不触发收费供应商、不写媒体/任务终态；媒体一律走 storyFilmLibrary。
 */

const PREFIX = '/api/visual-game/story-film/policy'

/**
 * @param {{ request: Function }} transport
 * @param {{ prefix?: string }} [options]
 */
export function createStoryFilmPolicyApi(transport, { prefix = PREFIX } = {}) {
  if (!transport || typeof transport.request !== 'function') {
    throw new Error('createStoryFilmPolicyApi: transport.request is required')
  }
  return {
    /**
     * 本人首免资格与（可选）当前 run/branch 的自动拍授权状态。
     * @param {{ run_id?: string|null, branch_id?: string|null }} [params]
     */
    status({ run_id = null, branch_id = null } = {}) {
      const params = new URLSearchParams()
      if (run_id !== null && run_id !== '') params.set('run_id', String(run_id))
      if (branch_id !== null && branch_id !== '') params.set('branch_id', String(branch_id))
      const query = params.toString()
      return transport.request('GET', `${prefix}/status${query ? `?${query}` : ''}`, { parse: parsePolicyStatus })
    },
    /**
     * 一次性领取首免弹窗。200 的 can_show=false 也是正常结果（不抛错）。
     * @param {{ episode_id: string, media_version: string, run_id: string, branch_id: string }} body
     */
    claimFirstFree(body) {
      return transport.request('POST', `${prefix}/first-free/claim`, {
        body: {
          episode_id: requireString(body?.episode_id, 'episode_id'),
          media_version: requireString(body?.media_version, 'media_version'),
          run_id: requireString(body?.run_id, 'run_id'),
          branch_id: requireString(body?.branch_id, 'branch_id'),
        },
        parse: parseClaimResult,
      })
    },
    /** 关闭回执：同 token 幂等，不生成第二条领取。 */
    closeFirstFree({ claim_token } = {}) {
      return transport.request('POST', `${prefix}/first-free/close`, {
        body: { claim_token: requireString(claim_token, 'claim_token') },
        parse: parseCloseResult,
      })
    },
    /** 自动拍状态：开关、本次上限、已开拍几集、暂停原因。 */
    autoStatus({ run_id, branch_id } = {}) {
      const params = new URLSearchParams({ run_id: requireString(run_id, 'run_id'), branch_id: requireString(branch_id, 'branch_id') })
      return transport.request('GET', `${prefix}/auto?${params.toString()}`, { parse: parseAutoShoot })
    },
    /** 开启自动拍：从第 1 回合起每 3 回合一集逐集拍，最多 max_episodes 集。 */
    enableAuto({ run_id, branch_id, max_episodes } = {}) {
      return transport.request('POST', `${prefix}/auto/enable`, {
        body: { run_id: requireString(run_id, 'run_id'), branch_id: requireString(branch_id, 'branch_id'), max_episodes: requirePositiveInteger(max_episodes, 'max_episodes') },
        parse: parseAutoShoot,
      })
    },
    /** 暂停只挡下一集开拍，已在拍的集照常完成。 */
    pauseAuto({ run_id, branch_id } = {}) {
      return transport.request('POST', `${prefix}/auto/pause`, {
        body: { run_id: requireString(run_id, 'run_id'), branch_id: requireString(branch_id, 'branch_id') },
        parse: parseAutoShoot,
      })
    },
  }
}

export function parsePolicyStatus(value) {
  if (!isObject(value) || !isObject(value.first_free) || !isObject(value.capabilities)) {
    throw new Error('Story film policy status response is invalid')
  }
  return {
    first_free: {
      status: requireString(value.first_free.status, 'first_free.status'),
      episode_id: optionalString(value.first_free.episode_id),
      submission_id: value.first_free.submission_id === null || value.first_free.submission_id === undefined ? null : requireNumber(value.first_free.submission_id),
      reserved_at: optionalString(value.first_free.reserved_at),
      consumed_at: optionalString(value.first_free.consumed_at),
      // C 的只读门禁字段：旧响应缺字段时解析为 null，调用方必须 fail closed（不得当成获准）。
      entry: optionalString(value.first_free.entry),
      lock_status: optionalString(value.first_free.lock_status),
      locked_here: optionalBoolean(value.first_free.locked_here),
      locked_elsewhere: optionalBoolean(value.first_free.locked_elsewhere),
      hint_allowed: optionalBoolean(value.first_free.hint_allowed),
      candidate_ordering_verified: optionalBoolean(value.first_free.candidate_ordering_verified),
      reason: optionalString(value.first_free.reason),
      media_ready: optionalBoolean(value.first_free.media_ready),
    },
    capabilities: {
      first_free_claim: requireString(value.capabilities.first_free_claim, 'capabilities.first_free_claim'),
      auto_event_engage: optionalString(value.capabilities.auto_event_engage),
      auto_execution: optionalString(value.capabilities.auto_execution),
      current_cost_model_version: optionalString(value.capabilities.current_cost_model_version),
      historical_backfill: value.capabilities.historical_backfill === true,
      retry: value.capabilities.retry === true,
      retake: optionalString(value.capabilities.retake),
    },
    auto: value.auto === null || value.auto === undefined ? null : value.auto,
    // 服务端钟的入场时刻（S01A 在场判定用）：旧响应没有该字段时为 null，调用方必须 fail closed，
    // 不得用设备 Date.now() 或更晚的详情时间冒充入场。
    server_now: optionalString(value.server_now),
  }
}

function parseClaimResult(value) {
  if (!isObject(value) || typeof value.can_show !== 'boolean') {
    throw new Error('Story film first-free claim response is invalid')
  }
  return {
    can_show: value.can_show,
    already_claimed: value.already_claimed === true,
    reason: optionalString(value.reason),
    claim_token: optionalString(value.claim_token),
  }
}

function parseCloseResult(value) {
  if (!isObject(value) || typeof value.closed !== 'boolean') {
    throw new Error('Story film first-free close response is invalid')
  }
  return { closed: value.closed }
}

/**
 * @param {unknown} value
 * @returns {{ status: string, max_episodes: number|null, started_episodes: number, paused_reason: string|null, points: number }}
 */
export function parseAutoShoot(value) {
  if (!isObject(value) || typeof value.status !== 'string') throw new Error('Story film auto status response is invalid')
  return {
    status: value.status,
    max_episodes: value.max_episodes === null ? null : requirePositiveInteger(value.max_episodes, 'max_episodes'),
    started_episodes: requireNonNegativeInteger(value.started_episodes, 'started_episodes'),
    paused_reason: optionalString(value.paused_reason),
    points: requirePositiveInteger(value.points, 'points'),
  }
}

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function requireString(value, field) {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`Story film policy field is invalid: ${field}`)
  }
  return value
}

function optionalString(value) {
  return value === null || value === undefined ? null : String(value)
}

/** 只读门禁布尔：非布尔（含缺失/字符串/数字）一律 null，由调用方 fail closed。 */
function optionalBoolean(value) {
  return value === true || value === false ? value : null
}

function requireNumber(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) throw new Error('Story film policy field is invalid: submission_id')
  return number
}

function requirePositiveInteger(value, field) {
  const number = Number(value)
  if (!Number.isSafeInteger(number) || number < 1) throw new Error(`Story film policy field is invalid: ${field}`)
  return number
}

function requireNonNegativeInteger(value, field) {
  const number = Number(value)
  if (!Number.isSafeInteger(number) || number < 0) throw new Error(`Story film policy field is invalid: ${field}`)
  return number
}

